import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SimpleEntityActions } from '@app/classes';
import { PermissionGuard } from '@app/guards';
import { AdminGuard } from '@app/guards/admin.guard';
import { RoutesWithOptions } from '@app/interfaces';
import { ManageCustomFields, ManageRolesAndPermissions } from '@settings/meta/module-feature-permissions.meta';
import { SettingsResolver } from '@settings/settings.resolver';
import {
    AssignEmployeesView,
    BillingShowView,
    CompanyLogoShowView,
    EmployeeIndexView,
    FieldGroupsIndexView,
    FieldGroupsStoreView,
    FieldsIndexView,
    FieldsShowView,
    FieldsStoreView,
    IntegrationIndexView,
    IntegrationIndexViewV2,
    InvoiceIndexView,
    NotificationsIndexView,
    PerformanceIndexView,
    RoleIndexView,
    RoleShowView,
    RoleUpdateView,
    XeroErrorsAndEventsLogView,
    XeroManageView,
} from '@settings/views';
import { FieldGroupsOrderingView } from '@settings/views/custom-fields/field-groups/ordering/field-groups-ordering.view';
import { SignInSettingsView } from './views/sign-in/sign-in.view';

const routes: RoutesWithOptions = [
    { path: '', pathMatch: 'full', redirectTo: 'notifications' },
    {
        path: 'notifications',
        canActivate: [AdminGuard],
        children: [
            {
                path: '',
                component: NotificationsIndexView,
            },
            {
                path: 'performance',
                component: PerformanceIndexView,
            },
        ],
    },
    {
        path: 'integrations-v2',
        canActivate: [AdminGuard],
        children: [
            { path: '', component: IntegrationIndexViewV2 },
            {
                path: 'xero',
                children: [
                    { path: 'manage', component: XeroManageView },
                    { path: 'errors-and-events-log', component: XeroErrorsAndEventsLogView },
                ],
            },
        ],
    },
    { path: 'integrations/:integration', component: IntegrationIndexView, canActivate: [AdminGuard] },
    {
        path: 'sign-in',
        component: SignInSettingsView,
    },
    {
        path: 'billing',
        canActivate: [PermissionGuard],
        data: {
            permission: [
                ...new SimpleEntityActions('companySubscription').all,
                ...new SimpleEntityActions('companySubscriptionCard').some('create'),
            ],
            checkAllPermissions: true,
        },
        children: [
            { path: '', component: BillingShowView, canActivate: [AdminGuard] },
            { path: 'invoices', component: InvoiceIndexView },
        ],
    },
    {
        path: 'roles',
        canActivate: [PermissionGuard],
        data: {
            permission: ManageRolesAndPermissions.permissions,
            checkAllPermissions: true,
        },
        children: [
            {
                path: '',
                component: RoleIndexView,
            },
            {
                path: ':role',
                component: RoleShowView,
                resolve: {
                    models: SettingsResolver,
                },
            },
            {
                path: ':role/permissions',
                component: RoleUpdateView,
                resolve: {
                    models: SettingsResolver,
                },
            },
            {
                path: ':role/employees',
                component: EmployeeIndexView,
                resolve: {
                    models: SettingsResolver,
                },
            },
        ],
    },
    {
        path: 'custom-fields',
        canActivate: [AdminGuard, PermissionGuard],
        data: {
            permission: ManageCustomFields.permissions,
            checkAllPermissions: true,
        },
        children: [
            {
                path: '',
                component: FieldsIndexView,
            },
            {
                path: 'fields',
                children: [
                    { path: '', component: FieldsIndexView },
                    { path: 'create', component: FieldsStoreView },
                    {
                        path: ':field',
                        component: FieldsShowView,
                        resolve: {
                            models: SettingsResolver,
                        },
                    },
                    {
                        path: ':field/edit',
                        component: FieldsStoreView,
                        resolve: {
                            models: SettingsResolver,
                        },
                    },
                    {
                        path: ':field/assign-employees',
                        component: AssignEmployeesView,
                        resolve: {
                            models: SettingsResolver,
                        },
                    },
                ],
            },
            {
                path: 'field-groups',
                children: [
                    { path: '', component: FieldGroupsIndexView },
                    {
                        path: ':fieldGroup/edit',
                        component: FieldGroupsStoreView,
                        resolve: {
                            models: SettingsResolver,
                        },
                    },
                    {
                        path: 'ordering',
                        component: FieldGroupsOrderingView,
                    },
                ],
            },
        ],
    },
    {
        path: 'company-logo',
        component: CompanyLogoShowView,
        canActivate: [AdminGuard],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class SettingsRouterModule {}
